import React, { ReactNode } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider } from '../contexts/AuthContext';
import { RealtimeDataProvider } from '../contexts/RealtimeDataContext';
import { ToastProvider } from '../contexts/ToastContext';
import { ThemeContextProvider } from '../contexts/ThemeContext';
import OrganizationProviderWrapper from './OrganizationProviderWrapper';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders Component
 *
 * Wraps the entire application with all necessary providers in the correct order:
 * 1. ThemeContextProvider - Material-UI theming with dark mode support
 * 2. ToastProvider - Toast notification system
 * 3. AuthProvider - User authentication and session management
 * 4. OrganizationProvider - Organization and permissions context
 * 5. RealtimeDataProvider - Real-time data synchronization
 *
 * This component ensures all contexts are properly nested and available
 * throughout the application.
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <ThemeContextProvider>
      <Toaster position="top-right" richColors />
      <ToastProvider>
        <AuthProvider>
          <OrganizationProviderWrapper>
            <RealtimeDataProvider>
              {children}
            </RealtimeDataProvider>
          </OrganizationProviderWrapper>
        </AuthProvider>
      </ToastProvider>
    </ThemeContextProvider>
  );
};

export default AppProviders;
